import React, { useState } from 'react';
import HeroSection from './HeroSection';
import Layout from './Layout';
import SupportCard from './SupportCard';
import { Button } from './ui/button';
import GetStartedCard from './GetStartedCard';
import {
  BadgeCheck,
  CalendarCheck,
  Check,
  Cloud,
  Flag,
  GraduationCap,
  LayoutGrid,
  Puzzle,
  Quote,
  QuoteIcon,
  Sparkles,
  Upload,
  UserCheck,
  X,
  XIcon,
  XSquareIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

export default function SimulationsLabsVsCTFDPage() {
  const [currentAccordion, setCurrentAccordion] = useState<string | null>('item-1');

  const recommendations = [
    {
      slug: 'Beyond_CTFd_Why_Modern_Cybersecurity_Training_Needs_More_Than_Flag_Capture',
      title: 'Beyond CTFd: Why Modern Cybersecurity Training Needs More Than Flag Capture',
      image:
        'https://images.simulationslabs.com/website+articles/Beyond+CTFd_+Why+Modern+Cybersecurity+Training+Needs+More+Than+Flag+Capture.png',
      author: 'Simulations Labs',
    },
    {
      slug: 'How_Much_Does_It_Really_Cost_to_Run_a_CTF_on_CTFd',
      title: 'How Much Does It Really Cost to Run a CTF on CTFd?',
      image:
        'https://images.simulationslabs.com/website+articles/How+Much+Does+It+Really+Cost+to+Run+a+CTF+on+CTFd_.png',
      author: 'Simulations Labs',
    },
    {
      slug: 'CTFd_FBCTF_and_rCTF_Which_OpenSource_CTF_Platform_Should_You_Deploy_in_2026',
      title: 'CTFd, FBCTF, and rCTF: Which Open-Source CTF Platform Should You Deploy in 2026',
      image:
        'https://images.simulationslabs.com/website+articles/CTFd%2C+FBCTF%2C+and+rCTF_+Which+Open-Source+CTF+Platform+Should+You+Deploy+in+2026.png',
      author: 'Simulations Labs',
    },
  ];
  const tableData = [
    {
      isHeader: true,
      rows: [
        { label: 'Features', values: ['Simulations Labs', 'CTFd'], isGray: true },
        { label: 'Delivering Courses', values: [<Check color="green" />, <X />] },
        { label: 'Unlimited Challenge Hosting', values: [<Check color="green" />, <X />] },
        { label: 'Unlimited Concurrent Competitions', values: [<Check color="green" />, <X />] },
        {
          label: 'Access to Pre-defined challenges (Simulations AI Copilot)',
          values: [<Check color="green" />, <X />],
        },
      ],
    },
  ];

  return (
    <div>
      <Layout>
        <div className="space-y-32 pb-20">
          <div className="flex w-full flex-col items-center justify-center gap-4 py-20 text-center">
            <span className="text-lightBlue text-[15px] font-bold uppercase">
              Simulations labs VS CTFD
            </span>
            <h1 className="mb-5 text-center text-5xl font-bold">
              Simulations Labs, the best CTFd
              <br /> alternative
            </h1>
            <div className="flex flex-col gap-4 md:flex-row">
              <Button variant="default">
                <Link to="https://app.simulationslabs.com/register/tenant">
                  Get Started for Free
                </Link>
              </Button>
              <Button variant="outline">
                <Link to="mailto:info@simulationslabs.com">Request a Demo</Link>
              </Button>
            </div>
            <div className="flex flex-col items-center gap-10 space-y-5 pt-7 md:flex-row md:space-y-0">
              <span className="flex items-center gap-2 rounded-full bg-white px-5 py-2 text-[15px]">
                <Check color="#1da2f2" /> No setup required
              </span>
              <span className="flex items-center gap-2 rounded-full bg-white px-5 py-2 text-[15px]">
                <Check color="#1da2f2" /> Ready-to-use challenges
              </span>
              <span className="flex items-center gap-2 rounded-full bg-white px-5 py-2 text-[15px]">
                <Check color="#1da2f2" /> Scales to hundreds of players
              </span>
            </div>
          </div>
          <div className="items-center justify-between gap-10 md:flex">
            <div id="demo" className="grid space-y-10">
              <div className="text-center">
                <h1 className="text-[42px] font-medium">What Makes Simulations Labs Different</h1>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-3">
                <GetStartedCard
                  icon={<LayoutGrid size={30} />}
                  title="User-Friendly Dashboard"
                  description="Manage competitions, challenges, and users through a clean, intuitive dashboard built for seamless control."
                />
                <GetStartedCard
                  icon={<Cloud size={30} />}
                  title="Managed Scalability and Security"
                  description="Run competitions on fully managed infrastructure that ensures reliability, security, and seamless scaling for teams, programs, and large-scale environments."
                />
                <GetStartedCard
                  icon={<Sparkles size={30} />}
                  title="AI Copilot + Expert Simulation Library"
                  description="Access a library of expert-created challenges using AI-powered search to quickly find challenges matching your scenario description."
                />
              </div>
            </div>
          </div>
          {/* Comparison Table Section */}
          <div className="mx-auto mt-32 max-w-7xl">
            <h1 className="py-10 text-center text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Compare Plan Features
            </h1>
            <div className="overflow-x-auto bg-white">
              <table className="min-w-full border-collapse border border-solid">
                <tbody>
                  {tableData.map((section, sectionIdx) => (
                    <React.Fragment key={sectionIdx}>
                      {section.category && (
                        <tr className={cn(section.isGray && 'bg-gray-50')}>
                          <td
                            colSpan={5}
                            className="bg-lightGray text-lightBlack px-6 py-4 font-medium"
                          >
                            {section.category}
                          </td>
                        </tr>
                      )}
                      {section.rows.map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className={cn('border-t border-gray-200', row.isGray && 'bg-gray-100')}
                        >
                          <td className="px-6 py-4 text-sm text-gray-700">{row.label}</td>
                          {row.values.map((value, valueIdx) => (
                            <td
                              key={valueIdx}
                              className="text-lightBlack border-l border-gray-200 px-6 py-4 text-center text-sm"
                            >
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <div className="mx-auto flex items-center justify-center rounded-full">
                                    <BadgeCheck className="text-white" fill="black" />
                                  </div>
                                ) : null
                              ) : (
                                <>
                                  {section.category === 'Bill per month' && row.oldValues && (
                                    <div className="text-gray-800" style={redLineStyle}>
                                      {row.oldValues[valueIdx]}
                                    </div>
                                  )}
                                  <div>{value}</div>
                                </>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-center">
              <Button variant="default" className="mt-10">
                <Link to="https://app.simulationslabs.com/register/tenant">
                  Get Started for Free
                </Link>
              </Button>
            </div>
          </div>

          <div className="items-center justify-between gap-20 text-left md:grid">
            <h1 className="text-center text-4xl font-bold">
              Trusted By Cybersecurity Community Leads
            </h1>
            <div className="mx-auto justify-center gap-4 gap-5 text-left md:grid md:grid-cols-2">
              <div className="bg-gray mb-2 flex flex-col space-y-5 p-10">
                <Quote size={20} className="text-gray-500" />
                <div className="pl-2">
                  <p className="mb-5 text-gray-500">
                    Simulations Labs has made it very easy to host a CTF. We only focus on creating
                    and uploading the challenges, and everything else gets taken care of. On the CTF
                    day, we didn’t need to worry about the site going down or getting attacked.
                  </p>
                  <h1 className="text-[17px] font-medium">Henock Negash</h1>
                  <span className="text-sm text-gray-500">Community Lead at Yekolo Temari</span>
                </div>
              </div>
              <div className="bg-gray flex flex-col space-y-5 p-10">
                <Quote size={20} className="text-gray-500" />
                <div className="pl-2">
                  <p className="mb-5 text-gray-500">
                    Simulations Labs is easy to set up, flexible, and backed by great 24/7 technical
                    support. Integrating AWS servers for our challenges was smooth and saved us a
                    lot of time. Even when I had trouble at the start, the support team guided me
                    quickly.
                  </p>
                  <h1 className="font-medium text-[17]">Morgan Maxwell</h1>
                  <span className="text-sm text-gray-500">
                    Founder at All Safe Africa Cybersecurity
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Recommended Articles */}
          {recommendations && recommendations.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-16">
              <div className="mb-7 text-left">
                <h2 className="mb-2 text-2xl font-bold text-gray-900">
                  Learn More: Simulations Labs vs CTFd
                </h2>
                <p className="text-gray-500">
                  Dive deeper into why Simulations Labs is a better alternative to CTFd
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {recommendations.map((blog: any) => (
                  <Link
                    key={blog.slug}
                    to={`/blogs/${blog.slug}`}
                    className="group overflow-hidden border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-lg"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-gray-900 transition-colors group-hover:text-blue-600">
                        {blog.title}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{blog.author}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </Layout>
    </div>
  );
}
